import { makeObservable, observable, computed, action } from "mobx";
import dayjs from "dayjs";
import { ImChat, isOptIn, isTemplateSent } from "@whatslly/types/tuvis-messaging/TuvisMessagingChats";
import { ImConversation } from "@whatslly/types/tuvis-messaging/TuvisMessagingConversations";
import { getPrettyPhone } from "@whatslly/commons/utils";
import { StatusEnum } from "@whatslly/types/tuvis-messaging/TuvisMessagingEnums";
import { ChannelType } from "@whatslly/types/tuvis-messaging/TuvisMessagingChannels";
import type { IDataStore } from "@whatslly/ui-components/Pages/TuvisMessaging/services/IDataStore";
export class IMChat implements ImChat {
  static cast(key: string, value: any) {
    if (key === "lastWaTemplateAt" || key === "lastWaMessageAt") return value ? new Date(value).getTime() : -1;
  }

  _id: string;
  orgId: string;
  firstName: string | undefined;
  lastName: string | undefined;
  phone: number;
  createdAt: string;
  updatedAt: string;
  lastWaTemplateAt: string | undefined;
  lastWaMessageAt: string | undefined;
  createdBy: "user" | "webhook";
  type: ChannelType;
  isArchived: boolean;
  phoneId: string;
  contactId: string | undefined;
  labels: string[] | undefined;
  currentConversationId: string | undefined;

  static dataStore: IDataStore;

  constructor(data: ImChat) {
    this._id = data._id;
    this.orgId = data.orgId;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.phone = data.phone;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.createdBy = data.createdBy;
    this.type = data.type;
    this.isArchived = data.isArchived;
    this.phoneId = data.phoneId;
    this.contactId = data.contactId;
    this.lastWaTemplateAt = data.lastWaTemplateAt;
    this.lastWaMessageAt = data.lastWaMessageAt;
    this.labels = data.labels;
    this.currentConversationId = data.currentConversationId;

    makeObservable<IMChat, "displayRealName">(this, {
      contactId: observable,
      isArchived: observable,
      lastWaTemplateAt: observable,
      lastWaMessageAt: observable,
      currentConversationId: observable,
      currentConversation: computed,
      isOptIn: computed,
      isTemplateSent: computed,
      contact: computed,
      displayName: computed,
      lastDate: computed,
      messages: computed,
      unreadMessages: computed,
      markAsRead: action,
      isDisplayNameExists: computed,
      displayRealName: computed,
      createdBy: observable,
    });
  }

  get currentConversation(): ImConversation | undefined {
    if (!this.currentConversationId) return undefined;
    return IMChat.dataStore.conversations.get(this.currentConversationId);
  }

  get isOptIn() {
    return isOptIn(this);
  }

  get isTemplateSent() {
    return isTemplateSent(this);
  }

  get contact() {
    return IMChat.dataStore.contacts.get(this.contactId ?? "");
  }

  private get displayRealName() {
    return this.contact?.displayName || `${this.firstName ?? ""} ${this.lastName ?? ""}`.trim();
  }

  get isDisplayNameExists() {
    return Boolean(this.displayRealName);
  }

  get displayName() {
    return this.displayRealName || getPrettyPhone(String(this.phone));
  }

  get lastDate() {
    return dayjs(this.messages.at(-1)?.createdAt ?? this.createdAt);
  }

  get messages() {
    return IMChat.dataStore.messages
      .filter((msg) => msg.phoneNumberId === this.phoneId && (this.phone === msg.recipientNumber || this.phone === msg.senderNumber))
      .map((message) => ({ ...message, isSent: message.direction === "internal" ? false : message.recipientNumber === this.phone, isInternal: message.direction === "internal" }));
  }

  get unreadMessages() {
    return this.messages.filter((msg) => msg.status !== StatusEnum.Read && msg.senderNumber === this.phone);
  }

  markAsRead() {
    this.unreadMessages.forEach(({ _id }) => {
      const msg = IMChat.dataStore.messages.get(_id);
      if (msg) msg.status = StatusEnum.Read;
    });
  }
}
